import { Incident, RiskAssessment, PermitToWork, ManagementOfChange, AuditInspection, ActionItem, User } from '../types';

const BASE_URL = 'https://api.titanemporium.co.za/api';
const API_BASE_URL = `${BASE_URL}/hsse`;

const getHeaders = (methodOverride?: string) => {
  const token = localStorage.getItem('titan_auth_token');
  const headers: any = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (methodOverride) {
    headers['X-HTTP-Method-Override'] = methodOverride;
  }
  return headers;
};

const handleResponse = async (response: Response) => {
  if (response.status === 204) return null;
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== '/') {
            localStorage.removeItem('titan_auth_token');
            localStorage.removeItem('titan_user_profile');
        }
    }
    throw new Error(error.message || `API error: ${response.status}`);
  }
  return response.json();
};

export const login = async (credentials: any) => {
    const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
    });
    return handleResponse(response);
};

export const fetchLookups = async (moduleCode: string): Promise<Record<string, string[]>> => {
    const response = await fetch(`${BASE_URL}/lookups/${moduleCode}`, {
        headers: getHeaders()
    });
    return handleResponse(response);
};

export const fetchUsers = async (): Promise<User[]> => {
    const response = await fetch(`${BASE_URL}/admin/users`, {
        headers: getHeaders()
    });
    return handleResponse(response);
};

export const createUser = async (userData: any): Promise<User> => {
    const response = await fetch(`${BASE_URL}/admin/users`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(userData),
    });
    return handleResponse(response);
};

export const updateUser = async (id: string, userData: any): Promise<User> => {
    const response = await fetch(`${BASE_URL}/admin/users/${id}`, {
        method: 'POST',
        headers: getHeaders('PUT'),
        body: JSON.stringify(userData),
    });
    return handleResponse(response);
};

export const fetchModules = async (): Promise<any[]> => {
    const response = await fetch(`${BASE_URL}/admin/modules`, {
        headers: getHeaders()
    });
    return handleResponse(response);
};

export const createLookup = async (moduleCode: string, name: string, options: any[]): Promise<any> => {
    const response = await fetch(`${BASE_URL}/admin/lookups/${moduleCode}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name, options }),
    });
    return handleResponse(response);
};

export const updateLookup = async (moduleCode: string, name: string, options: any[]): Promise<any> => {
    const response = await fetch(`${BASE_URL}/admin/lookups/${moduleCode}/${name}`, {
        method: 'POST',
        headers: getHeaders('PUT'),
        body: JSON.stringify({ options }),
    });
    return handleResponse(response);
};

const createApiHandlers = <T>(endpoint: string) => ({
  fetch: async (): Promise<T[]> => {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
        headers: getHeaders()
    });
    return handleResponse(response);
  },
  create: async (data: Partial<T>): Promise<T> => {
    const response = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  update: async (id: string, data: Partial<T>): Promise<T> => {
    const response = await fetch(`${API_BASE_URL}/${endpoint}/${id}`, {
      method: 'POST',
      headers: getHeaders('PUT'),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/${endpoint}/${id}`, {
      method: 'POST',
      headers: getHeaders('DELETE')
    });
    return handleResponse(response);
  }
});

export const fetchIncidents = createApiHandlers<Incident>('incidents').fetch;
export const createIncident = createApiHandlers<Incident>('incidents').create;
export const updateIncident = createApiHandlers<Incident>('incidents').update;
export const deleteIncident = createApiHandlers<Incident>('incidents').delete;

export const fetchRisks = createApiHandlers<RiskAssessment>('risks').fetch;
export const createRisk = createApiHandlers<RiskAssessment>('risks').create;
export const updateRisk = createApiHandlers<RiskAssessment>('risks').update;
export const deleteRisk = createApiHandlers<RiskAssessment>('risks').delete;

export const fetchPermits = createApiHandlers<PermitToWork>('permits').fetch;
export const createPermit = createApiHandlers<PermitToWork>('permits').create;
export const updatePermit = createApiHandlers<PermitToWork>('permits').update;
export const deletePermit = createApiHandlers<PermitToWork>('permits').delete;

export const fetchMocs = createApiHandlers<ManagementOfChange>('mocs').fetch;
export const createMoc = createApiHandlers<ManagementOfChange>('mocs').create;
export const updateMoc = createApiHandlers<ManagementOfChange>('mocs').update;
export const deleteMoc = createApiHandlers<ManagementOfChange>('mocs').delete;

export const fetchAudits = createApiHandlers<AuditInspection>('audits').fetch;
export const createAudit = createApiHandlers<AuditInspection>('audits').create;
export const updateAudit = createApiHandlers<AuditInspection>('audits').update;
export const deleteAudit = createApiHandlers<AuditInspection>('audits').delete;

export const fetchActions = createApiHandlers<ActionItem>('actions').fetch;
export const createAction = createApiHandlers<ActionItem>('actions').create;
export const updateAction = createApiHandlers<ActionItem>('actions').update;
export const deleteAction = createApiHandlers<ActionItem>('actions').delete;
